import { Product } from '@@types'
import { zodResolver } from '@hookform/resolvers/zod'
import { Controller, useForm } from 'react-hook-form'
import { Trans, useTranslation } from 'react-i18next'
import { z } from 'zod'

import { useSaveProductReview } from '@entities/Review/api/saveProductReview.gql'

import { useModalStore } from '@features/modal'
import { uploadFile } from '@features/upload'

import { URL_PAGE_POLICY } from '@shared/contants'
import { LocalizedLink as Link } from '@shared/lib/locale'
import Button from '@shared/ui/button'
import ErrorMessage from '@shared/ui/error-message'
import ImageWithBg from '@shared/ui/image-with-bg'
import RatingStars from '@shared/ui/rating/stars'
import Textarea from '@shared/ui/text-area'
import { useToastStore } from '@shared/ui/toast/useToastStore'
import UploadFile from '@shared/ui/upload-file'

import styles from './styles.module.scss'

const reviewSchema = z.object({
  rating: z.number().min(1, 'grade_required'),
  text: z.string().optional(),
  files: z.array(z.object({ id: z.number(), url: z.string() })).optional(),
})

type ReviewFormData = z.infer<typeof reviewSchema>;

export interface ReviewProductCreateProps {
  data: Product;
}

export default function ReviewProductCreate (props: ReviewProductCreateProps) {
  const [ fetchSaveProductReview ] = useSaveProductReview()
  const { closeModal } = useModalStore()
  const addToast = useToastStore((state) => state.addToast)
  const { id, name, preview } = props.data
  const { t: ta } = useTranslation('auth')
  const { t: tc } = useTranslation('common')

  const {
    handleSubmit,
    control,
    register,
    setValue,
    formState: { errors },
  } = useForm<ReviewFormData>({
    resolver: zodResolver(reviewSchema),
    defaultValues: {
      rating: 0,
      text: '',
      files: [],
    },
  })

  const handleFilesAccepted = async (accepted: File[]) => {
    try {
      const uploaded = await Promise.all(
        accepted.map((file) => uploadFile('product-review-image', file)),
      )
      setValue('files', uploaded, { shouldValidate: true, shouldDirty: true })
    }
    catch (err) {
      console.error(err)
    }
  }

  const onSubmit = async (data: ReviewFormData) => {
    try {
      await fetchSaveProductReview({
        variables: {
          newData: {
            rating: data.rating,
            product_id: id,
            comment: data.text ?? '',
            images: data.files?.map((file) => String(file.id)) ?? [],
          },
          upsert: true,
        },
      })
      addToast({
        message: tc('review.moderation_notice'),
        variant: 'success',
      })
      closeModal('reviewCreate')
    }
    catch (error) {
      console.error('Save product review error: ', error)
      addToast({
        message: tc('error_submit'),
        variant: 'error',
      })
    }
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className={styles.create}>
      <header className={styles.create__header}>
        <ImageWithBg
          className={styles.create__image}
          src={preview?.url as string}
        />
        <h3 className={styles.create__title}>
          <span>{name}</span>
        </h3>

        <Controller
          control={control}
          name="rating"
          render={({ field }) => (
            <div className={styles.create__rating}>
              <RatingStars value={field.value} onChange={field.onChange} />
              {errors.rating && (
                <ErrorMessage>
                  {tc(`review.${errors.rating.message}`)}
                </ErrorMessage>
              )}
            </div>
          )}
        />
      </header>

      <div className={styles.create__content}>
        <Textarea
          inputClassName={styles.create__textarea}
          placeholder="Отзыв о товаре"
          {...register('text')}
        />

        <UploadFile
          accept={[ '.jpg', '.jpeg', '.png' ]}
          onFileAccepted={handleFilesAccepted}
          errorText="Допустимы только изображения JPG/PNG, до 5 файлов, не более 5MB каждый"
        />
      </div>

      <Button size="large" type="submit">
        {tc('review.add')}
      </Button>

      <p className={styles.create__policy}>
        <Trans
          i18nKey={ta('code.privacy.note')}
          components={[
            <></>,
            <Link
              to={URL_PAGE_POLICY}
              target="_blank"
              rel="noopener noreferrer"
            />,
          ]}
        />
      </p>
    </form>
  )
}
